import { useEffect, useLayoutEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { hasPersistedSession } from '../auth/persisted-session';

/**
 * Upper bound on a single `fetchAuthSession()` call (issue #2595). On idle-return
 * Amplify silently refreshes tokens; if that fetch hangs, the Authenticator state
 * machine never transitions out of `'configuring'` and the app shows an unbounded
 * spinner. We race every session check against this timeout so a hang is always
 * resolved — in the worst case by sending the user to `/login`.
 */
export const SESSION_FETCH_TIMEOUT_MS = 8_000;

/**
 * Minimum hidden duration before a tab-return is treated as a real idle-return
 * (issue #2595). Without this, `fetchAuthSession()` would fire on every brief
 * refocus (tab flick, alt-tab) — wasteful and unnecessary. Only after the tab has
 * been hidden at least this long do we re-validate the session.
 */
export const IDLE_THRESHOLD_MS = 5 * 60 * 1_000;

/**
 * Thrown when a `fetchAuthSession()` call exceeds {@link SESSION_FETCH_TIMEOUT_MS}.
 * Deliberately module-private: it is only ever constructed and caught inside this
 * file, and exporting a non-component, non-constant binding from a component
 * module breaks React Fast Refresh (`react-refresh/only-export-components`).
 */
class SessionTimeoutError extends Error {
    constructor() {
        super('Session fetch timed out');
        this.name = 'SessionTimeoutError';
    }
}

/**
 * Races a promise against a {@link SESSION_FETCH_TIMEOUT_MS} timeout, clearing the
 * timer on settle so no stray timer leaks. Rejects with {@link SessionTimeoutError}
 * if the timeout wins.
 */
function withTimeout<T>(promise: Promise<T>): Promise<T> {
    let timer: ReturnType<typeof setTimeout>;
    const timeout = new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new SessionTimeoutError()), SESSION_FETCH_TIMEOUT_MS);
    });
    return Promise.race([promise, timeout]).finally(() => clearTimeout(timer)) as Promise<T>;
}

/**
 * Always-mounted, render-nothing component that provides bounded auth-session
 * recovery (issue #2595).
 *
 * Renders no UI. It must be mounted INSIDE the always-present `<Router>` (it uses
 * `useNavigate`) and regardless of `authStatus`. It is deliberately NOT placed in
 * `useCurrentUser` / `AuthenticatedLayout`, which mount only after
 * `authStatus === 'authenticated'` — during the stuck `'configuring'` state those
 * components do not exist, so a recovery handler there would never run.
 *
 * Behavior:
 * - On mount, runs a bounded session check so a hung session-fetch at startup
 *   surfaces (worst case: the user is sent to `/login`, never an infinite spinner).
 * - On `visibilitychange` to `visible` after the tab was hidden at least
 *   {@link IDLE_THRESHOLD_MS}, re-validates the session, again bounded by the
 *   timeout. A short refocus does nothing.
 * - If the session check times out OR rejects, performs a local `signOut()` then
 *   navigates to `/login` (replacing, preserving the deep-link intent in
 *   `state.from`).
 *
 * Gating (issue #2579): recovery only fires when the session is plausibly
 * recoverable — `authStatus` is `'configuring'` (possibly a hung startup
 * refresh) or `'authenticated'` (an idle-return refresh that may now be
 * hanging). When Amplify has cleanly settled to `'unauthenticated'` (a
 * never-authenticated cold start), SessionRecovery STANDS DOWN: `ProtectedRoute`
 * already drives the `/login` redirect, and a competing `signOut()` + navigate
 * here would race that redirect and leave the URL stranded on the protected
 * path (the #2579 bug).
 *
 * Token-presence gate (issue #3194): the #2579 gate above stands down only for
 * the SETTLED `'unauthenticated'` state, but a never-authenticated cold start
 * begins in `'configuring'` and its startup `fetchAuthSession()` REJECTS while
 * `authStatus` is still `'configuring'` — so the un-gated code would recover and
 * race ProtectedRoute anyway (the configuring-then-settle variant). Mount-time
 * recovery only makes sense for a REAL persisted session whose refresh is hung
 * (#2595), which requires tokens to exist. So the mount check additionally
 * stands down unless {@link hasPersistedSession} finds durable evidence of a
 * persisted session (Cognito tokens or a signer JWT).
 */
function SessionRecovery() {
    const navigate = useNavigate();
    const location = useLocation();
    const { authStatus } = useAuthenticator((context) => [context.authStatus]);
    // Timestamp (ms) the tab last became hidden; null while visible.
    const hiddenSinceRef = useRef<number | null>(null);
    // Guards against concurrent recovery runs (e.g. mount check still in flight
    // when a visibilitychange fires).
    const checkingRef = useRef(false);
    // Always-current mirrors of authStatus + location so the once-only effect
    // below reads the latest values at recovery time without re-subscribing
    // listeners on every render. Synced in depsless effects (run after every
    // commit) rather than during render — writing a ref during render is
    // flagged by react-hooks/refs (#3347): a render can be discarded or
    // double-invoked without committing, so a ref write during render isn't
    // guaranteed to reflect the committed value.
    const authStatusRef = useRef(authStatus);
    const locationRef = useRef(location);
    // useLayoutEffect, NOT useEffect: these mirrors are read at line ~175 inside
    // the catch after `await withTimeout(fetchAuthSession())` -- the #2579
    // stand-down gate, which exists precisely because authStatus can transition
    // while a bounded fetch is in flight. A passive effect commits on a later
    // scheduler task, widening the window in which that read sees a stale
    // 'configuring' and calls recoverToLogin() -- the stranded-URL race behind
    // #2579 and #3194. A layout effect commits synchronously during the commit
    // phase, before paint and before any later-queued microtask, which is the
    // closest safe equivalent to the render-phase write react-hooks/refs forbids.
    useLayoutEffect(() => {
        authStatusRef.current = authStatus;
    });
    useLayoutEffect(() => {
        locationRef.current = location;
    });

    useEffect(() => {
        let cancelled = false;

        const recoverToLogin = async () => {
            try {
                await signOut();
            } catch (error) {
                // The session is already hung/failed — surface to login regardless.
                console.error('Error signing out during session recovery:', error);
            }
            if (!cancelled) {
                // Replace (no spurious history entry) and carry the deep-link
                // intent so post-login lands on the originally requested route —
                // matching ProtectedRoute's redirect contract (issue #2579).
                navigate('/login', { replace: true, state: { from: locationRef.current } });
            }
        };

        // True when Amplify has cleanly settled to `unauthenticated` (a
        // never-authenticated cold start). In that state ProtectedRoute owns the
        // /login redirect and SessionRecovery must stand down (issue #2579).
        // Read through a function so the value is re-evaluated on each call —
        // `authStatus` can transition while a bounded fetch is in flight.
        const isSettledUnauthenticated = () => authStatusRef.current === 'unauthenticated';

        const checkSession = async ({ requireExistingSession = false } = {}) => {
            // Stand down for a cleanly-settled unauthenticated cold start
            // (issue #2579). Only recover a plausibly-recoverable session
            // (configuring — possibly a hung startup refresh — or authenticated).
            if (isSettledUnauthenticated()) {
                return;
            }
            // Mount-time token-presence gate (issue #3194): the CONFIGURING-then-
            // settle variant of the #2579 race. A never-authenticated cold start
            // begins in `'configuring'` (NOT the settled `'unauthenticated'` the
            // #2579 gate above keys on), and its `fetchAuthSession()` REJECTS —
            // driving the catch below into recoverToLogin() while `authStatus` is
            // still `'configuring'`. The resulting `signOut()` Hub churn unmounts
            // the conditionally-rendered <Routes> subtree and drops
            // ProtectedRoute's pending <Navigate to="/login" replace> before it
            // commits, stranding the URL on the protected path. Mount-time
            // recovery (#2595) only makes sense when a REAL persisted session's
            // refresh is hung — which requires tokens to exist. If there is no
            // durable evidence of a persisted session (no Cognito tokens, no
            // signer JWT), stand down and let ProtectedRoute own the /login
            // redirect. The idle-return path is unaffected (it never had this
            // never-authenticated variant).
            if (requireExistingSession && !hasPersistedSession()) {
                return;
            }
            if (checkingRef.current) {
                return;
            }
            checkingRef.current = true;
            try {
                await withTimeout(fetchAuthSession());
                // Healthy session — nothing to do; the Authenticator drives the UI.
            } catch {
                // Re-check the gate: if Amplify settled to a clean
                // `unauthenticated` while the bounded fetch was in flight,
                // ProtectedRoute now owns the redirect — stand down (issue #2579).
                if (!cancelled && !isSettledUnauthenticated()) {
                    await recoverToLogin();
                }
            } finally {
                checkingRef.current = false;
            }
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                hiddenSinceRef.current = Date.now();
                return;
            }
            // Became visible. Only re-check after a real idle period.
            const hiddenSince = hiddenSinceRef.current;
            hiddenSinceRef.current = null;
            if (hiddenSince !== null && Date.now() - hiddenSince >= IDLE_THRESHOLD_MS) {
                void checkSession();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        // Bounded session check at mount so a hung startup fetch also surfaces.
        // `requireExistingSession` gates recovery on durable evidence of a real
        // persisted session (issue #3194) so a never-authenticated cold start
        // does not signOut()+navigate and race ProtectedRoute's /login redirect.
        void checkSession({ requireExistingSession: true });

        return () => {
            cancelled = true;
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
        // navigate is stable across renders (react-router); run effect once.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return null;
}

export default SessionRecovery;
